<template>
  <div class="flex flex-col gap-6 lg:gap-8">
    <div class="flex flex-wrap items-end justify-between gap-4">
      <div class="flex flex-col gap-2">
        <h2 class="text-sm-h3 font-semibold text-white-constant lg:text-lg-h3">{{ $t('blog.top.title') }}</h2>
      </div>
      <div class="flex items-center gap-2">
        <button
          type="button"
          :aria-label="$t('blog.top.previous')"
          class="flex items-center justify-center size-10 rounded-full border border-gray-80 text-white-constant transition-colors hover:bg-violet-0 hover:border-violet-0"
          @click="goTo(activeIndex - 1)"
        >
          <icon
            name="icon:chevron"
            class="rotate-180"
          />
        </button>
        <button
          type="button"
          :aria-label="$t('blog.top.next')"
          class="flex items-center justify-center size-10 rounded-full border border-gray-80 text-white-constant transition-colors hover:bg-violet-0 hover:border-violet-0"
          @click="goTo(activeIndex + 1)"
        >
          <icon name="icon:chevron" />
        </button>
      </div>
    </div>

    <div
      class="overflow-hidden rounded-2xl"
      @mouseenter="isPaused = true"
      @mouseleave="isPaused = false"
      @focusin="isPaused = true"
      @focusout="isPaused = false"
    >
      <div
        class="flex transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
        :style="{ transform: `translateX(-${activeIndex * 100}%)` }"
      >
        <div
          v-for="(article, index) in articles"
          :key="article.id"
          class="shrink-0 w-full"
        >
          <NuxtLinkLocale
            :to="`/blog/${article.slug}`"
            class="group grid h-full bg-gray-80 lg:grid-cols-[1fr_30rem]"
          >
            <div class="flex flex-col gap-6 p-6 md:p-8 lg:gap-8 lg:p-10">
              <div class="flex flex-wrap items-center gap-3">
                <span class="py-1.5 px-3.5 rounded-999 bg-violet-0 text-14 leading-140 font-bold text-white-constant">
                  {{ $t('blog.top.rank', { n: index + 1 }) }}
                </span>
                <span class="py-1.5 px-3.5 rounded-999 border border-gray-10 text-14 leading-140 font-semibold text-gray-40">
                  {{ tagTitle(article) }}
                </span>
              </div>
              <div class="flex flex-col gap-3 lg:gap-4">
                <h3 class="text-sm-h4 font-semibold text-white-constant transition-colors group-hover:text-violet-10">
                  {{ article.title }}
                </h3>
                <p class="text-b2 text-gray-0 line-clamp-3">{{ excerptOf(article) }}</p>
              </div>
              <div class="mt-auto flex flex-wrap items-center gap-x-2 gap-y-1 text-14 leading-140 text-gray-0">
                <span>{{ article.author }}</span>
                <span class="size-1 rounded-full bg-gray-10" />
                <span>{{ formatBlogDate(article.publishedAt, locale) }}</span>
                <span class="size-1 rounded-full bg-gray-10" />
                <span>{{ $t('blog.readingTime', { n: article.readingTime }) }}</span>
              </div>
            </div>
            <!--
              Обложка занимает всю ширину своей колонки: раньше вокруг неё
              оставались поля, и картинка 1920×1080 сидела маленьким пятном в
              углу карточки. Номер лежит поверх, а не отодвигает изображение.
            -->
            <!--
              Обложка занимает колонку целиком — и по ширине, и по высоте.
              Пропорцию слоту не задаём: 16:9 при этой ширине ниже карточки, и
              снизу оставалась пустая полоса. Кадрирование по центру срезает
              максимум по краям, а не половину картинки, как было при узкой
              колонке.
            -->
            <div
              class="hidden h-full w-full lg:block"
              :style="coverStyle(article)"
            />
          </NuxtLinkLocale>
        </div>
      </div>
    </div>

    <div class="flex items-center gap-2">
      <button
        v-for="(article, index) in articles"
        :key="article.id"
        type="button"
        :aria-label="$t('blog.top.slide', { n: index + 1 })"
        :class="[
          'h-1 rounded-999 transition-all duration-300',
          index === activeIndex ? 'w-10 bg-violet-0' : 'w-5 bg-gray-80 hover:bg-gray-10'
        ]"
        @click="goTo(index)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { blogCover, blogCoverUrl } from '@/utils/blog-cover'
import { blogPrimaryTag, blogTagTitle } from '@/utils/blog-tags'
import {
  BLOG_CATEGORIES,
  BLOG_PLACEHOLDER_EXCERPT,
  type BlogArticle
} from '@/constants/blog'

const props = defineProps<{
  articles: BlogArticle[]
}>()

const SLIDE_INTERVAL = 3000

/*
 * Анонс берём из статьи. Константа осталась запасным вариантом: у материала,
 * которому анонс не написали, карточка не должна выходить с пустой строкой.
 */
/*
 * Анонс — функция от статьи, а не одно значение: в ленте материалов несколько,
 * и computed без аргумента показывал бы у всех один и тот же текст.
 */
const excerptOf = (article: BlogArticle) =>
  String((article as { excerpt?: string | null }).excerpt ?? '').trim() || BLOG_PLACEHOLDER_EXCERPT

const activeIndex = ref<number>(0)
const isPaused = ref<boolean>(false)

let timer: ReturnType<typeof setInterval> | null = null

const { t, locale } = useI18n()

/** Обложка из кабинета, если её выбрали; иначе рисованная. */
const coverStyle = (article: BlogArticle) => {
  const url = blogCoverUrl(article.cover, useRuntimeConfig().public.apiUrl as string)

  return url
    ? {
        backgroundImage: `url("${url}")`,
        /*
         * Обложки грузят в 1920×1080, а колонка выше своей ширины — при
         * `cover` срезало края вместе с зашитым в картинку заголовком:
         * «Серебро 1980 vs 2026» превращалось в «еребро 1980 vs 2026».
         *
         * `contain` показывает кадр целиком. Свободные полосы сверху и снизу
         * остаются фоном карточки, поэтому читаются как поле, а не как рамка.
         */
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      }
    : { background: blogCover(article.id, 'card') }
}

/** Бейдж ленты: подпись первого тега, а без тегов — прежняя рубрика. */
const tagTitle = (article: BlogArticle) => {
  const tag = blogPrimaryTag(article.tags)

  return tag ? blogTagTitle(tag, locale.value) : categoryLabel(article)
}

const categoryLabel = (article: BlogArticle) => {
  const key = BLOG_CATEGORIES.find((category) => category.id === article.category)?.labelKey
  return key ? t(key) : ''
}

const goTo = (index: number) => {
  const total = props.articles.length

  if (!total) return

  activeIndex.value = (index + total) % total
  restart()
}

const stop = () => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

const start = () => {
  if (!import.meta.client) return

  stop()

  timer = setInterval(() => {
    if (isPaused.value || props.articles.length < 2) return

    activeIndex.value = (activeIndex.value + 1) % props.articles.length
  }, SLIDE_INTERVAL)
}

// Ручное переключение сдвигает отсчёт: иначе слайд, выбранный за миг
// до срабатывания таймера, показывается доли секунды.
const restart = () => start()

// Смена фильтра меняет набор слайдов — старый индекс может указывать в пустоту.
watch(() => props.articles, () => {
  activeIndex.value = 0
  start()
})

onMounted(start)
onBeforeUnmount(stop)
</script>
